import type { TreemapDrilldownSegment } from '../../../filters/utils/treemapDrilldownLevels';
import type { DrilldownFilter } from '../../DataProvider/composition/NestedCompositionTreemapDrilldownProvider';

/**
 * Shared plumbing for the composition drilldown charts (nested treemap,
 * stacked bar): the in-memory drill stack and its mapping onto the
 * composition_v3 drilldown filter.
 */

// Designer greys for the v1.1 drilldown "Other" bucket. Scoped here so the
// shared/legacy Other colour (getOtherColor) keeps its previous value.
export const COMPOSITION_DRILLDOWN_OTHER_COLOR = '#84848F';
export const COMPOSITION_DRILLDOWN_OTHER_COLOR_DARK = '#C8C8CA';

export const compositionDrilldownOtherColor = (
  theme: 'light' | 'dark'
): string =>
  theme === 'dark'
    ? COMPOSITION_DRILLDOWN_OTHER_COLOR_DARK
    : COMPOSITION_DRILLDOWN_OTHER_COLOR;

export type DrilldownEntry = {
  level: string;
  id: number | string;
  name: string;
  segment: TreemapDrilldownSegment;
  color?: string;
};

export function drilldownEntryToFilter(
  stack: DrilldownEntry[]
): DrilldownFilter {
  const entry = stack[stack.length - 1];
  if (entry === undefined) {
    throw new Error('drilldownEntryToFilter called with empty stack');
  }
  switch (entry.segment) {
    case 'geography':
      return {
        segment: 'geography',
        level: entry.level as
          | 'region'
          | 'country'
          | 'state'
          | 'msa'
          | 'city'
          | 'county',
        id: Number(entry.id),
      };
    case 'role':
      return {
        segment: 'role',
        levelId: entry.level as
          | 'role_k7'
          | 'role_k150'
          | 'role_k1500'
          | 'role_k5000'
          | 'role_k15000',
        id: String(entry.id),
      };
    case 'rics':
      return {
        segment: 'rics',
        level: entry.level as 'rics_k10' | 'rics_k50' | 'rics_k400' | 'company',
        id: Number(entry.id),
      };
    case 'skills': {
      const nestedId = stack
        .filter((s) => s.segment === 'skills')
        .map((s) => String(s.id))
        .join('_');
      return {
        segment: 'skills',
        level: entry.level as
          | 'skill_0'
          | 'skill_1'
          | 'skill_2'
          | 'skill_3'
          | 'skill_4',
        id: nestedId,
      };
    }
    case 'activities':
      return {
        segment: 'activities',
        level: entry.level as
          | 'activity_k150'
          | 'activity_k500'
          | 'activity_k1500'
          | 'activity_k5000',
        id: Number(entry.id),
      };
  }
}

/** Colour of the drilled-into parent: the last stack entry that captured one. */
export function getDrilldownColor(
  stack: readonly { color?: string }[]
): string | undefined {
  for (let i = stack.length - 1; i >= 0; i--) {
    const color = stack[i]?.color;
    if (color !== undefined) return color;
  }
  return undefined;
}
